import React, { Fragment, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import FilterAccordian from '../filterAccordian';
import Breadcrumb from '../common/breadcrumb';
import { userRequest } from '../../auth/requestMethods';
import { Users } from 'react-feather';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomTable from '../tables/MuiDataGrid';
import { toast } from 'react-toastify';

const Manage = ({ history }) => {
  useEffect(() => {
    document.title = 'PFSA | Field Workers';
  }, []);
  const [modalState, setModalState] = React.useState({
    update: false,
    open: false,
  });
  const [perPage, setPerPage] = React.useState(10);
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [appliedFilters, setAppliedFilters] = React.useState([]);
  const [dataIsFetching, setDataIsFetching] = React.useState(true);
  const [tableData, setTableData] = React.useState({
    count: 0,
    fieldWorkers: [],
  });

  const {
    register,
    handleSubmit,
    watch,
    reset,
    trigger,
    // clearErrors,
    getValues,
    formState: { errors, isSubmitted },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  React.useEffect(() => {
    trigger();
    const subscription = watch((value, { name }) => trigger(name));
    return () => subscription.unsubscribe();
  }, [watch, trigger]);

  React.useEffect(() => {
    fetchTableData();
  }, [statusFilter, perPage, page]);

  function fetchTableData() {
    setDataIsFetching(true);
    userRequest()
      .get(
        `/field-workers?per_page=${perPage}&page=${page}${
          statusFilter || statusFilter === false ? '&is_active=' + statusFilter : ''
        }`
      )
      .then(({ data }) => {
        setTableData({
          count: data.total,
          fieldWorkers: data.fieldWorkers,
        });
        if (statusFilter || statusFilter === false) setAppliedFilters(['Status']);
        else setAppliedFilters([]);
        setDataIsFetching(false);
      })
      .catch(({ response }) => {
        console.log(response);
        setDataIsFetching(false);
      });
  }

  function setStatus(id, data) {
    userRequest()
      .post(`/field-workers/update/${id}`, data)
      .then(() => {
        fetchTableData();
      })
      .catch(() => {});
  }

  function meetFilterConditions(data, isUpdate = true) {
    if (!isUpdate) {
      if (tableData.count === perPage) return false;
    }
    if (statusFilter === 'ALL') {
      return true;
    }
    if (appliedFilters.length > 0) return statusFilter === data.is_active;

    return true;
  }

  const actionMethods = {
    toggleEdit: (row) => {
      setModalState({ open: true, update: row.id });
      reset(row);
      // clearErrors();
    },
    toggleActive: (row) => setStatus(row.id, { is_active: !row.is_active }),
  };

  const columns = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'surname',
      headerName: 'Surname',
      flex: 1,
    },
    {
      field: 'is_active',
      headerName: 'Status',
      headerAlign: 'center',
      width: 110,
      renderCell: ({ row }) =>
        row.is_active ? (
          <Typography className="font-success mx-auto">Active</Typography>
        ) : (
          <Typography className="font-danger mx-auto">Inactive</Typography>
        ),
    },
  ];

  function getFilters() {
    if (statusFilter === true) {
      return ['Status: Active'];
    } else if (statusFilter === false) {
      return ['Status: Inactive'];
    } else {
      return [];
    }
  }

  const onSubmit = (data) => {
    if (data !== '') {
      toast.info(`${modalState.update ? 'Updating...' : 'Creating...'}`, {
        isLoading: true,
        position: 'bottom-center',
        autoClose: 5000,
        theme: 'colored',
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        toastId: 'create',
      });
      let api = modalState.update ? `/field-workers/update/${modalState.update}` : '/field-workers';

      userRequest()
        .post(api, data)
        .then(() => {
          toast.dismiss('create');
          toast.success(`Successfully ${modalState.update ? 'updated' : 'created'}`, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
          fetchTableData();
          setModalState({ update: true, open: false });
          reset(defaultValues, {});
        })
        .catch(({ response }) => {
          toast.dismiss('create');
          toast.error(response.data.message, {
            position: 'bottom-center',
            autoClose: 5000,
            closeOnClick: true,
            draggable: true,
          });
        });
    }
  };

  return (
    <Fragment>
      <Breadcrumb icon={<Users />} title="Manage" parent="Field Worker" link="manage" />

      <div className="container-fluid">
        <FilterAccordian filters={getFilters()}>
          <div className="form-group row mx-0">
            <FormControl>
              <InputLabel id="demo-simple-select-helper-label">Status</InputLabel>
              <Select
                name="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                labelId="demo-simple-select-helper-label"
                id="demo-simple-select-helper"
                label="Status"
                style={{
                  fontStyle: statusFilter === 'All' ? 'italic' : 'normal',
                }}
              >
                <MenuItem value={'All'} style={{ fontStyle: 'italic' }}>
                  All
                </MenuItem>
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </div>
        </FilterAccordian>
      </div>
      <button
        onClick={() => {
          setModalState({ open: true, update: false });
          // clearErrors();
        }}
        className="btn btn-secondary m-15"
        type="submit"
      >
        <i className="fa fa-plus fa-xl" aria-hidden="true"></i>
      </button>
      <Modal
        isOpen={modalState.open}
        toggle={() => {
          setModalState({ open: false, update: false });
          reset(defaultValues, {});
        }}
        size="lg"
        centered={true}
      >
        <ModalHeader
          toggle={() => {
            setModalState({ open: false, update: false });
            reset(defaultValues, {});
          }}
        >
          {modalState.update ? 'Edit' : 'Create'} Field Worker{' '}
        </ModalHeader>
        <ModalBody>
          <div className="card-body">
            <form className="theme-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="form-group row">
                <TextField
                  {...register(`name`)}
                  error={!!(errors.name?.message && isSubmitted && getValues().name === '')}
                  label="Name"
                  variant="outlined"
                  // helperText={errors.name?.message && isSubmitted ? errors.name.message : ''}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="form-group row">
                <TextField
                  {...register(`surname`)}
                  error={!!(errors.surname?.message && isSubmitted && getValues().surname === '')}
                  label="Surname"
                  variant="outlined"
                  // helperText={errors.surname?.message && isSubmitted ? errors.surname.message : ''}
                  inputProps={{
                    style: {
                      color: 'black',
                    },
                  }}
                />
              </div>
              <div className="d-flex justify-content-start my-4">
                {modalState.update && (
                  <FormGroup>
                    <FormControlLabel
                      control={<Switch {...register(`is_active`)} checked={watch('is_active')} />}
                      label="Status"
                      sx={{ margin: 0 }}
                      labelPlacement="start"
                    />
                  </FormGroup>
                )}
              </div>
              <div>
                <button className="btn btn-primary me-1" type="submit">
                  {modalState.update ? 'Save' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </ModalBody>
      </Modal>
      <div className="container-fluid">
        <CustomTable
          rows={tableData.fieldWorkers}
          actions={actionMethods}
          columns={columns}
          setPageSize={setPerPage}
          setPage={setPage}
          rowCount={tableData.count}
          pageSize={perPage}
          loading={dataIsFetching}
        />
      </div>
    </Fragment>
  );
};

const defaultValues = {
  name: '',
  surname: '',
  is_active: true,
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required'),
  surname: Yup.string().required('Required'),
  is_active: Yup.boolean().required(),
});

export default Manage;
